import React, { useCallback } from 'react';
import { AuthenticatedActions, DownloadableItemSource } from 'types';
import { getDownloadableItem, getIsDAWPlugin } from 'helpers';
import { DownloadType } from 'types/enums';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import {
    getDownloadIcon,
    getDownloadLabel,
} from 'components/CommonUi/DownloadButton/DownloadButton.helpers';
import { IconProps } from '@landr/maestro';
import { t } from '@lingui/macro';

const useDownloadButton = ({
    item,
    onClick,
    label,
    showIcon = true,
}: {
    item: DownloadableItemSource;
    onClick?: () => void;
    label: string;
    showIcon?: boolean;
}): {
    handleClick: (event?: React.MouseEvent<Element>) => void;
    isLoading: boolean;
    isDownloadable: boolean;
    icon: React.FC<IconProps> | undefined;
    buttonLabel: string;
    userAction: any;
} => {
    const downloadableItem = getDownloadableItem(item);

    const icon = showIcon
        ? getDownloadIcon(downloadableItem.isDownloaded, getIsDAWPlugin())
        : undefined;

    const buttonLabel = getDownloadLabel(
        item,
        downloadableItem,
        getIsDAWPlugin(),
        label,
    );

    const isDownloadable = downloadableItem.type !== DownloadType.Unknown;

    const userAction = {
        type: AuthenticatedActions.downloadItem,
        description: t`download`,
        args: [downloadableItem],
    };

    const {
        startPurchase,
        isLoading: purchaseLoading,
        queuedItemIDs,
    } = usePurchaseControllerContext();

    const isLoading =
        purchaseLoading && queuedItemIDs.includes(downloadableItem.id);

    const handleClick = useCallback(() => {
        startPurchase({ item: downloadableItem });
        onClick && onClick();
    }, [startPurchase, downloadableItem, onClick]);

    return {
        handleClick,
        isLoading,
        isDownloadable,
        icon,
        buttonLabel,
        userAction,
    };
};

export default useDownloadButton;
